import * as Sentry from '@sentry/react';
import _ from 'lodash';
import moment from 'moment';

//constants
import { MOMENT_TIME_FORMAT } from '../../../constants/date_format';
import { FACILITIES_VIEW_TYPE } from '../../../constants'
import { HEIGHT_DEFAULT, WIDTH_DEFAULT, WIDTH_DEFAULT_WEEKDAYS, WIDTH_TIME } from '../../../constants/timetableLayout';

//models
import { RGL } from '../../../models/timetable';
import { timetableEntryIds } from '../../../helpers/sentry';
import { xAxis, yAxis } from '../../../models/gridLayout';

/*
 * Mon-Fri for Academic view
 * Mon-Sun for Facilities view
 */
export const getTimetableGridWeekdays = (viewType) => {
    let weekdays = _.dropRight(moment.weekdays(true), 2);

    if (viewType && viewType === FACILITIES_VIEW_TYPE) weekdays = [...moment.weekdays().slice(1), moment.weekdays()[0]];

    return weekdays;
};

/*
 * number of days being displayed + 1 for time column
 */
export const getTimetableGridColumns = (viewType) => {
    return getTimetableGridWeekdays(viewType).length + 1;
};

/*
 * Generate the second row for a Timetable
 * - Includes the 'Time' text
 * - Monday to Friday for weekdays
 */
export const generateFirstRow = (defaultLayout, viewType) => {
    let widthWeekDays = WIDTH_DEFAULT_WEEKDAYS;

    let xAxisValue = 0; //starting at the far left side
    let i = defaultLayout.length;
    let xAxisArr = [];

    const weekdays = getTimetableGridWeekdays(viewType);

    //empty element above times (0,0)
    defaultLayout.push(new RGL(i, xAxisValue, 0, WIDTH_TIME, HEIGHT_DEFAULT, { type: 'emptyCell' }));

    //x-axis for title starts at 2
    xAxisValue = xAxisValue + 1;

    weekdays.forEach((day) => {
        ++i;

        //Element for each day of the week
        defaultLayout.push(new RGL(i, xAxisValue, 0, widthWeekDays, HEIGHT_DEFAULT, { type: 'weekday', value: day }));
        xAxisArr.push(new xAxis(xAxisValue, day));
        xAxisValue = xAxisValue + widthWeekDays;
    });

    return xAxisArr;
};

/*
 * Add timeslots to the defaultLayout
 */
export const generateTimeSlots = (defaultLayout, config) => {
    let startDur = moment(config.minStart, MOMENT_TIME_FORMAT);
    let endDur = moment(config.maxEnd, MOMENT_TIME_FORMAT);

    let i = defaultLayout.length;
    let timeYAxis = 1;
    let run = true;

    let yAxisArr = [];

    while (run) {
        //Element for each time slot
        defaultLayout.push(
            new RGL(i, 0, timeYAxis, WIDTH_TIME, HEIGHT_DEFAULT, {
                type: 'time',
                value: startDur.clone(),
            }),
        );

        yAxisArr.push(new yAxis(timeYAxis, startDur));

        ++i;
        ++timeYAxis;
        startDur.add(30, 'minutes');

        if (startDur.get('hours') > endDur.get('hours')) {
            run = false;
        }
    }
    return yAxisArr;
};

/*
 * Timetable: SetTimetable, InstructorTimetable or RoomTimetable
 */
export const getXAxis = (timetable, xAxisArr) => {
    const xAxis = xAxisArr.find((x) => x.dayOfWeek === timetable.dayOfWeek);

    if (xAxis) {
        return xAxis.value;
    } else {
        throw RangeError('xAxis is out of bounds');
    }
};

export const getYAxis = (timetable, yAxisArr) => {
    const yAxis = yAxisArr.find((y) => y.time.format(MOMENT_TIME_FORMAT) === timetable.timeIn.format(MOMENT_TIME_FORMAT));

    if (yAxis) {
        return yAxis.value;
    } else {
        throw RangeError('yAxis is out of bounds');
    }
};

/*
 * Width is 2
 */
export const getWidth = () => {
    return WIDTH_DEFAULT_WEEKDAYS;
};

/*
 * RETURN: height of the tile
 * - Height is always twice the number of hours the class is scheduled for
 */
export const getHeight = (timetable) => {
    const timeIn = moment.utc(timetable.timeIn, 'HH:mm');
    const timeOut = moment.utc(timetable.timeOut, 'HH:mm');

    const duration = moment.duration(timeOut.diff(timeIn));

    const classHours = duration.hours();
    const classMinutes = duration.minutes();

    return (classHours + hoursToAdd(classMinutes)) * 2;
};

/*
 * RETURNS: int
 * - the value used to round up the hours
 */
const hoursToAdd = (minutes) => {
    let hoursToAdd = 0;

    if (minutes > 15 && minutes <= 30) {
        hoursToAdd = 0.5;
    } else if (minutes > 30) {
        hoursToAdd = 1;
    }

    return hoursToAdd;
};

/*
 * //TODO: FIX
 * For now, set the height of the conflicts to 3
 */
export const getHeightConflict = () => {
    return 3;
};

/*
 * All Conflicts are shown below the 'Conflicts' header
 */
export const setYAxisConflict = (rglLayout, conflictHeaderYAxis) => {
    let yAxis = conflictHeaderYAxis + 1;
    let height = 0;
    let prevDayOfWeek = rglLayout[0].settings.value.dayOfWeek;

    rglLayout.forEach((el) => {
        //reset the height so conflicts for a new day are right under the conflict header
        if (el.settings.value.dayOfWeek !== prevDayOfWeek) {
            prevDayOfWeek = el.settings.value.dayOfWeek;
            height = 0;
        }

        el.y = yAxis + height;
        height = height + el.h;
    });
};

/*
 * Generate the timetable's layout
 * - Index is the length of the defaultLayout
 */
export const generateTimetableLayout = (timetables, index, xAxisArr, yAxisArr, type = 'timetable') => {
    let i = index;
    const rglLayout = [];

    timetables.forEach((timetable) => {
        try {
            const xAxis = getXAxis(timetable, xAxisArr);
            const yAxis = getYAxis(timetable, yAxisArr);
            const width = getWidth();
            const height = getHeight(timetable);
            rglLayout.push(new RGL(i, xAxis, yAxis, width, height, { type, value: timetable }));
            ++i;
        } catch (error) {
            Sentry.captureException(error, (scope) => {
                scope.setContext('data', {
                    timetable: timetableEntryIds(timetable),
                    timetableIDs: timetables.map((t) => t.id),
                    index,
                    xAxisArr,
                    yAxisArr,
                    type,
                });
                return scope;
            });
        }
    });

    return rglLayout;
};

/*
 * The header's y value depends on the number of time slots + 1
 */
export const getConflictHeader = (i, yAxis, viewType) => {
    const headerWidth = getTimetableGridColumns(viewType) * WIDTH_DEFAULT + WIDTH_TIME;
    return new RGL(i, 0, yAxis, headerWidth, HEIGHT_DEFAULT, { type: 'conflict', value: 'Conflict' });
};

/*
 * Generate the Conflict layout under the 'Conflicts' header
 */
export const generateConflictsLayout = (timetablesLayout, yAxisArrLength, conflicts, xAxisArr, viewType) => {
    const lastElement = _.last(timetablesLayout);
    let i = _.parseInt(lastElement.i, 0) + 1;
    const yAxisConflictHeader = yAxisArrLength + 1;

    const conflictHeader = getConflictHeader(i, yAxisConflictHeader, viewType);
    timetablesLayout.push(conflictHeader);

    //Get the conflicts
    const rglLayout = [];

    conflicts.forEach((conflict) => {
        ++i;
        const xAxis = getXAxis(conflict, xAxisArr);

        const width = getWidth();
        const height = getHeightConflict();
        rglLayout.push(new RGL(i, xAxis, 0, width, height, { type: 'timetable', value: conflict }));
    });

    //FIXME: Temp Fix
    setYAxisConflict(rglLayout, yAxisConflictHeader);

    //Combine Timetables with conflicts
    return timetablesLayout.concat(rglLayout);
};

/*
 * Number of columns to display. Depends on the number of weekdays to display (viewType)
 * Always take up the entire screen regardless of screen size
 */
export const getColumnsObject = (viewType) => {
    const columns = getTimetableGridColumns(viewType);
    return { lg: columns, md: columns, sm: columns, xs: columns, xxs: columns };
};
